import { useEffect, useState } from 'react'

const WORKER_SRC = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js'

export interface PdfPageImage {
  src: string
  width: number
  height: number
}

type Status = 'idle' | 'loading' | 'ready' | 'error'

interface CacheEntry {
  pages: PdfPageImage[]
  status: Status
  listeners: Set<() => void>
  started: boolean
}

/** One entry per PDF URL: the document is fetched and rendered once per page load. */
const cache = new Map<string, CacheEntry>()

function getEntry(url: string): CacheEntry {
  let entry = cache.get(url)
  if (!entry) {
    entry = { pages: [], status: 'idle', listeners: new Set(), started: false }
    cache.set(url, entry)
  }
  return entry
}

function notify(entry: CacheEntry) {
  entry.listeners.forEach((listener) => listener())
}

async function renderDocument(url: string, targetWidth: number) {
  const entry = getEntry(url)
  if (entry.started) return
  entry.started = true
  entry.status = 'loading'
  notify(entry)

  try {
    const pdfjsLib = await import('pdfjs-dist')
    pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SRC
    const pdf = await pdfjsLib.getDocument(url).promise
    for (let index = 1; index <= pdf.numPages; index += 1) {
      const page = await pdf.getPage(index)
      const baseViewport = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: targetWidth / baseViewport.width })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Canvas indisponible')
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      await page.render({ canvasContext: context, viewport }).promise
      entry.pages = [
        ...entry.pages,
        { src: canvas.toDataURL('image/jpeg', 0.9), width: canvas.width, height: canvas.height },
      ]
      page.cleanup()
      notify(entry)
    }
    entry.status = 'ready'
  } catch {
    entry.status = 'error'
    entry.started = false
  }
  notify(entry)
}

/**
 * Renders the pages of a PDF to images once `enabled` becomes true
 * (e.g. when the section approaches the viewport). Pages appear progressively.
 */
export function usePdfPageImages(url: string, enabled: boolean, targetWidth = 1100) {
  const entry = getEntry(url)
  const [, setVersion] = useState(0)

  useEffect(() => {
    const listener = () => setVersion((version) => version + 1)
    entry.listeners.add(listener)
    return () => {
      entry.listeners.delete(listener)
    }
  }, [entry])

  useEffect(() => {
    if (enabled) renderDocument(url, targetWidth)
  }, [enabled, url, targetWidth])

  return { pages: entry.pages, status: entry.status }
}
